/**
 * 모니터링 메트릭 Mock Store
 * Prometheus API (/monitoring/metrics/*) 목업 데이터
 */
import type { PrometheusMatrixResult, PrometheusVectorResult } from '@/features/monitoring/api/types';

const DEMO_MEMORY_TOTAL_BYTES = 16 * 1024 * 1024 * 1024;
const DEMO_ERROR_RATE_POD_BASE_VALUES = [0.02, 0.035, 0.05] as const;
const DEMO_ERROR_RATE_TOTAL_BASE_VALUE = 0.095;
const DEMO_GPU_MEMORY_METRICS = [
  {
    hostname: 'demo-worker-a100-01',
    gpu: '0',
    device: 'nvidia0',
    gpuInstanceId: '0',
    gpuInstanceProfile: '1g.10gb',
    uuid: 'GPU-demo-a100-01-0',
    modelName: 'NVIDIA A100-SXM4-40GB',
    node: 'demo-worker-a100-01',
    exportedNamespace: 'demo-cyclone',
    exportedPod: 'training-runner-7f8d9',
    exportedContainer: 'trainer',
    baseMemoryMib: 10240,
  },
  {
    hostname: 'demo-worker-a100-01',
    gpu: '1',
    device: 'nvidia1',
    gpuInstanceId: '1',
    gpuInstanceProfile: '2g.20gb',
    uuid: 'GPU-demo-a100-01-1',
    modelName: 'NVIDIA A100-SXM4-40GB',
    node: 'demo-worker-a100-01',
    exportedNamespace: 'demo-cyclone',
    exportedPod: 'feature-pipeline-65c4b',
    exportedContainer: 'worker',
    baseMemoryMib: 14336,
  },
  {
    hostname: 'demo-worker-l4-01',
    gpu: '0',
    device: 'nvidia0',
    gpuInstanceId: '0',
    gpuInstanceProfile: 'full',
    uuid: 'GPU-demo-l4-01-0',
    modelName: 'NVIDIA L4',
    node: 'demo-worker-l4-01',
    exportedNamespace: 'demo-cyclone',
    exportedPod: 'inference-server-554c9',
    exportedContainer: 'inference',
    baseMemoryMib: 6144,
  },
] as const;

/**
 * 시간 범위에 대한 시계열 데이터 생성
 */
const generateTimeSeriesData = (
  baseValue: number,
  variance: number,
  start: number,
  end: number,
  step: number
): [number, string][] => {
  const data: [number, string][] = [];
  for (let time = start; time <= end; time += step) {
    const randomVariance = (Math.random() - 0.5) * variance;
    const value = Math.max(0, baseValue + randomVariance);
    data.push([time, value.toFixed(4)]);
  }
  return data;
};

/**
 * GPU memory mock 데이터 생성
 * DCGM_FI_DEV_FB_USED는 MiB 단위로 내려오며 UI에서 GB로 환산한다.
 */
export const getGpuMemoryMockData = (start: number, end: number, step: number): PrometheusMatrixResult[] => {
  return DEMO_GPU_MEMORY_METRICS.map((gpuMetric) => ({
    metric: {
      __name__: 'DCGM_FI_DEV_FB_USED',
      Hostname: gpuMetric.hostname,
      hostname: gpuMetric.hostname,
      gpu: gpuMetric.gpu,
      device: gpuMetric.device,
      GPU_I_ID: gpuMetric.gpuInstanceId,
      GPU_I_PROFILE: gpuMetric.gpuInstanceProfile,
      UUID: gpuMetric.uuid,
      modelName: gpuMetric.modelName,
      node: gpuMetric.node,
      namespace: gpuMetric.exportedNamespace,
      exported_namespace: gpuMetric.exportedNamespace,
      exported_pod: gpuMetric.exportedPod,
      exported_container: gpuMetric.exportedContainer,
    },
    values: generateTimeSeriesData(gpuMetric.baseMemoryMib, 768, start, end, step),
  }));
};

export const getGpuMemoryInstantMockData = (): PrometheusVectorResult[] => {
  const now = Math.floor(Date.now() / 1000);

  return DEMO_GPU_MEMORY_METRICS.map((gpuMetric) => ({
    metric: {
      __name__: 'DCGM_FI_DEV_FB_USED',
      Hostname: gpuMetric.hostname,
      hostname: gpuMetric.hostname,
      gpu: gpuMetric.gpu,
      device: gpuMetric.device,
      GPU_I_ID: gpuMetric.gpuInstanceId,
      GPU_I_PROFILE: gpuMetric.gpuInstanceProfile,
      UUID: gpuMetric.uuid,
      modelName: gpuMetric.modelName,
      node: gpuMetric.node,
      namespace: gpuMetric.exportedNamespace,
      exported_namespace: gpuMetric.exportedNamespace,
      exported_pod: gpuMetric.exportedPod,
      exported_container: gpuMetric.exportedContainer,
    },
    value: [now, Math.max(0, gpuMetric.baseMemoryMib + (Math.random() - 0.5) * 768).toFixed(4)],
  }));
};

/**
 * CPU 메트릭 mock 데이터 생성
 */
export const getCpuMockData = (
  query: string,
  queryType: 'instant' | 'range',
  start?: number,
  end?: number,
  step?: number
): PrometheusVectorResult[] | PrometheusMatrixResult[] => {
  const now = Math.floor(Date.now() / 1000);
  const pods = ['cyclone-api-server', 'cyclone-ml-worker-1', 'cyclone-ml-worker-2'];

  if (query.includes('by (pod)')) {
    if (queryType === 'range') {
      return pods.map((pod, index) => ({
        metric: {
          pod: `demo-cyclone-${pod}-abc123`,
          namespace: 'demo-cyclone',
        },
        values: generateTimeSeriesData(0.5 + index * 0.3, 0.2, start || now - 3600, end || now, step || 60),
      })) as PrometheusMatrixResult[];
    }
    return pods.map((pod, index) => ({
      metric: {
        pod: `demo-cyclone-${pod}-abc123`,
        namespace: 'demo-cyclone',
      },
      value: [now, (0.5 + index * 0.3 + (Math.random() - 0.5) * 0.1).toFixed(4)],
    })) as PrometheusVectorResult[];
  }

  if (query.includes('resource_requests') || query.includes('resource_limits')) {
    return [
      {
        metric: { namespace: 'demo-cyclone' },
        value: [now, '8.0'],
      },
    ] as PrometheusVectorResult[];
  }

  if (queryType === 'range') {
    return [
      {
        metric: { namespace: 'demo-cyclone' },
        values: generateTimeSeriesData(1.2, 0.3, start || now - 3600, end || now, step || 60),
      },
    ] as PrometheusMatrixResult[];
  }

  return [
    {
      metric: { namespace: 'demo-cyclone' },
      value: [now, (1.2 + (Math.random() - 0.5) * 0.2).toFixed(4)],
    },
  ] as PrometheusVectorResult[];
};

/**
 * Memory 메트릭 mock 데이터 생성
 */
export const getMemoryMockData = (
  query: string,
  queryType: 'instant' | 'range',
  start?: number,
  end?: number,
  step?: number
): PrometheusVectorResult[] | PrometheusMatrixResult[] => {
  const now = Math.floor(Date.now() / 1000);
  const pods = ['cyclone-api-server', 'cyclone-ml-worker-1', 'cyclone-ml-worker-2'];

  if (query.includes('by (pod)')) {
    if (queryType === 'range') {
      return pods.map((pod, index) => ({
        metric: {
          pod: `demo-cyclone-${pod}-abc123`,
          namespace: 'demo-cyclone',
        },
        values: generateTimeSeriesData(
          (2.5 + index * 1.0) * 1024 * 1024 * 1024,
          0.5 * 1024 * 1024 * 1024,
          start || now - 3600,
          end || now,
          step || 60
        ),
      })) as PrometheusMatrixResult[];
    }
    return pods.map((pod, index) => ({
      metric: {
        pod: `demo-cyclone-${pod}-abc123`,
        namespace: 'demo-cyclone',
      },
      value: [now, ((2.5 + index * 1.0 + (Math.random() - 0.5) * 0.3) * 1024 * 1024 * 1024).toFixed(0)],
    })) as PrometheusVectorResult[];
  }

  if (query.includes('resource_requests') || query.includes('resource_limits')) {
    return [
      {
        metric: { namespace: 'demo-cyclone' },
        value: [now, DEMO_MEMORY_TOTAL_BYTES.toString()],
      },
    ] as PrometheusVectorResult[];
  }

  if (queryType === 'range') {
    return [
      {
        metric: { namespace: 'demo-cyclone' },
        values: generateTimeSeriesData(
          6.5 * 1024 * 1024 * 1024,
          1.0 * 1024 * 1024 * 1024,
          start || now - 3600,
          end || now,
          step || 60
        ),
      },
    ] as PrometheusMatrixResult[];
  }

  return [
    {
      metric: { namespace: 'demo-cyclone' },
      value: [now, ((6.5 + (Math.random() - 0.5) * 0.5) * 1024 * 1024 * 1024).toFixed(0)],
    },
  ] as PrometheusVectorResult[];
};

/**
 * Disk I/O 메트릭 mock 데이터 생성
 */
export const getDiskMockData = (
  query: string,
  queryType: 'instant' | 'range',
  start?: number,
  end?: number,
  step?: number
): PrometheusVectorResult[] | PrometheusMatrixResult[] => {
  const now = Math.floor(Date.now() / 1000);
  const pods = ['cyclone-api-server', 'cyclone-ml-worker-1', 'cyclone-ml-worker-2'];

  const isRead = query.includes('reads');
  const baseValue = isRead ? 1500 : 800;

  if (query.includes('by (pod)')) {
    if (queryType === 'range') {
      return pods.map((pod, index) => ({
        metric: {
          pod: `demo-cyclone-${pod}-abc123`,
          namespace: 'demo-cyclone',
        },
        values: generateTimeSeriesData(
          (baseValue + index * 200) * 1024,
          300 * 1024,
          start || now - 3600,
          end || now,
          step || 60
        ),
      })) as PrometheusMatrixResult[];
    }
    return pods.map((pod, index) => ({
      metric: {
        pod: `demo-cyclone-${pod}-abc123`,
        namespace: 'demo-cyclone',
      },
      value: [now, ((baseValue + index * 200 + (Math.random() - 0.5) * 200) * 1024).toFixed(0)],
    })) as PrometheusVectorResult[];
  }

  if (queryType === 'range') {
    return [
      {
        metric: { namespace: 'demo-cyclone' },
        values: generateTimeSeriesData(baseValue * 1024, 400 * 1024, start || now - 3600, end || now, step || 60),
      },
    ] as PrometheusMatrixResult[];
  }

  return [
    {
      metric: { namespace: 'demo-cyclone' },
      value: [now, ((baseValue + (Math.random() - 0.5) * 300) * 1024).toFixed(0)],
    },
  ] as PrometheusVectorResult[];
};

/**
 * Network I/O 메트릭 mock 데이터 생성
 */
export const getNetworkMockData = (
  query: string,
  queryType: 'instant' | 'range',
  start?: number,
  end?: number,
  step?: number
): PrometheusVectorResult[] | PrometheusMatrixResult[] => {
  const now = Math.floor(Date.now() / 1000);
  const pods = ['cyclone-api-server', 'cyclone-ml-worker-1', 'cyclone-ml-worker-2'];

  const isReceive = query.includes('receive');
  const baseValue = isReceive ? 2500 : 1200;

  if (query.includes('by (pod)')) {
    if (queryType === 'range') {
      return pods.map((pod, index) => ({
        metric: {
          pod: `demo-cyclone-${pod}-abc123`,
          namespace: 'demo-cyclone',
        },
        values: generateTimeSeriesData(
          (baseValue + index * 400) * 1024,
          500 * 1024,
          start || now - 3600,
          end || now,
          step || 60
        ),
      })) as PrometheusMatrixResult[];
    }
    return pods.map((pod, index) => ({
      metric: {
        pod: `demo-cyclone-${pod}-abc123`,
        namespace: 'demo-cyclone',
      },
      value: [now, ((baseValue + index * 400 + (Math.random() - 0.5) * 300) * 1024).toFixed(0)],
    })) as PrometheusVectorResult[];
  }

  if (queryType === 'range') {
    return [
      {
        metric: { namespace: 'demo-cyclone' },
        values: generateTimeSeriesData(baseValue * 1024, 600 * 1024, start || now - 3600, end || now, step || 60),
      },
    ] as PrometheusMatrixResult[];
  }

  return [
    {
      metric: { namespace: 'demo-cyclone' },
      value: [now, ((baseValue + (Math.random() - 0.5) * 400) * 1024).toFixed(0)],
    },
  ] as PrometheusVectorResult[];
};

/**
 * Error Rate 메트릭 mock 데이터 생성
 */
export const getErrorRateMockData = (
  query: string,
  queryType: 'instant' | 'range',
  start?: number,
  end?: number,
  step?: number
): PrometheusVectorResult[] | PrometheusMatrixResult[] => {
  const now = Math.floor(Date.now() / 1000);
  const pods = ['cyclone-api-server', 'cyclone-ml-worker-1', 'cyclone-ml-worker-2'];

  if (query.includes('by (pod)')) {
    if (queryType === 'range') {
      return pods.map((pod, index) => ({
        metric: {
          pod: `demo-cyclone-${pod}-abc123`,
          namespace: 'demo-cyclone',
        },
        values: generateTimeSeriesData(
          DEMO_ERROR_RATE_POD_BASE_VALUES[index] ?? DEMO_ERROR_RATE_POD_BASE_VALUES[0],
          0.012,
          start || now - 3600,
          end || now,
          step || 60
        ),
      })) as PrometheusMatrixResult[];
    }
    return pods.map((pod, index) => ({
      metric: {
        pod: `demo-cyclone-${pod}-abc123`,
        namespace: 'demo-cyclone',
      },
      value: [
        now,
        (
          (DEMO_ERROR_RATE_POD_BASE_VALUES[index] ?? DEMO_ERROR_RATE_POD_BASE_VALUES[0]) +
          (Math.random() - 0.5) * 0.01
        ).toFixed(4),
      ],
    })) as PrometheusVectorResult[];
  }

  if (queryType === 'range') {
    return [
      {
        metric: { namespace: 'demo-cyclone' },
        values: generateTimeSeriesData(
          DEMO_ERROR_RATE_TOTAL_BASE_VALUE,
          0.03,
          start || now - 3600,
          end || now,
          step || 60
        ),
      },
    ] as PrometheusMatrixResult[];
  }

  return [
    {
      metric: { namespace: 'demo-cyclone' },
      value: [now, (DEMO_ERROR_RATE_TOTAL_BASE_VALUE + (Math.random() - 0.5) * 0.02).toFixed(4)],
    },
  ] as PrometheusVectorResult[];
};

/**
 * 쿼리 문자열 분석하여 적절한 mock 데이터 반환
 */
export const getPrometheusInstantMockData = (query: string): PrometheusVectorResult[] => {
  if (query.includes('DCGM_FI_DEV_FB_USED') && !query.includes('DCGM_FI_DEV_FB_FREE')) {
    return getGpuMemoryInstantMockData();
  }
  if (query.includes('resource="memory"') || query.includes('pod_memory') || query.includes('container_memory')) {
    return getMemoryMockData(query, 'instant') as PrometheusVectorResult[];
  }
  if (
    query.includes('container_cpu_usage') ||
    query.includes('pod_cpu') ||
    query.includes('kube_pod_container_resource')
  ) {
    return getCpuMockData(query, 'instant') as PrometheusVectorResult[];
  }
  if (query.includes('error')) {
    return getErrorRateMockData(query, 'instant') as PrometheusVectorResult[];
  }
  if (query.includes('container_fs_reads') || query.includes('container_fs_writes')) {
    return getDiskMockData(query, 'instant') as PrometheusVectorResult[];
  }
  if (query.includes('container_network')) {
    return getNetworkMockData(query, 'instant') as PrometheusVectorResult[];
  }

  return [];
};

/**
 * Range 쿼리에 대한 mock 데이터 반환
 */
export const getPrometheusRangeMockData = (
  query: string,
  start: number,
  end: number,
  step: number
): PrometheusMatrixResult[] => {
  if (query.includes('DCGM_FI_DEV_FB_USED') && !query.includes('DCGM_FI_DEV_FB_FREE')) {
    return getGpuMemoryMockData(start, end, step);
  }
  if (query.includes('resource="memory"') || query.includes('pod_memory') || query.includes('container_memory')) {
    return getMemoryMockData(query, 'range', start, end, step) as PrometheusMatrixResult[];
  }
  if (query.includes('container_cpu_usage') || query.includes('pod_cpu')) {
    return getCpuMockData(query, 'range', start, end, step) as PrometheusMatrixResult[];
  }
  if (query.includes('error')) {
    return getErrorRateMockData(query, 'range', start, end, step) as PrometheusMatrixResult[];
  }
  if (query.includes('container_fs_reads') || query.includes('container_fs_writes')) {
    return getDiskMockData(query, 'range', start, end, step) as PrometheusMatrixResult[];
  }
  if (query.includes('container_network')) {
    return getNetworkMockData(query, 'range', start, end, step) as PrometheusMatrixResult[];
  }

  return [];
};
